import React, { useCallback, useEffect, useRef, useState } from 'react';

import { di } from 'react-magnetic-di';
import type { useMutation, UseMutationConfig } from 'react-relay';
import type { Disposable, MutationParameters } from 'relay-runtime';

import { fg } from '@atlaskit/platform-feature-flags';
import { useInteractionContext } from '@atlaskit/react-ufo/interaction-context';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import { setInteractionError } from '@atlaskit/react-ufo/set-interaction-error';
import traceUFOPress from '@atlaskit/react-ufo/trace-press';
import {
	getTraceIds,
	startCapturingTraceIds,
	stopCapturingTraceIds,
} from '@atlassian/relay-traceid';

import { logException } from '../sentry';

type ExtraParams<T extends MutationParameters> = {
	interactionName: string;
	/**
	 * Use the generated operation name from the graphql file
	 */
	operationName: string;
	/**
	 * If false, the interaction will not be marked as error on complete
	 * despite errors in the mutation response.
	 */
	shouldSetInteractionError?: (response: T['response']) => boolean;
};

const initialShouldSetInteractionError = () => true;

const noop = () => {};

const renderNothing = (): React.ReactNode => null;

/**
 * Wraps a relay mutation hook and adds UFO hold and error handling.
 *
 * The UFO hold is registered imperatively against the surrounding
 * `UFOInteractionContext` for the exact duration of each mutation, so the
 * caller does NOT need to render anything for tracking to work.
 *
 * On unmount any in-flight hold is released so the interaction does not get
 * stuck holding forever.
 */
export const useMutationWithUFO = <T extends MutationParameters>(
	useMutationHook: ReturnType<typeof useMutation<T>>,
	{
		interactionName,
		operationName,
		shouldSetInteractionError = initialShouldSetInteractionError,
	}: ExtraParams<T>,
): {
	commitMutation: (config: UseMutationConfig<T>) => Disposable;
	isError: boolean;
	isMutating: boolean;
	/**
	 * @private
	 * @deprecated When the `studio_observability_imperative_ufo_hold` gate is
	 * enabled, the hold is driven imperatively from inside this hook via the
	 * surrounding `UFOInteractionContext` and rendering is no longer required
	 * for tracking. This function becomes a no-op in that mode and will be
	 * removed in a future release. While the gate is off, you must still
	 * render `renderUFOHold()` somewhere in your tree.
	 */
	renderUFOHold: () => React.ReactNode;
} => {
	di();
	const [commitMutation, isMutating] = useMutationHook;
	const [isError, setIsError] = useState(false);
	const interactionContext = useInteractionContext();
	const releaseHoldRef = useRef<() => void>(noop);

	// Make sure we never leak a hold past unmount.
	useEffect(
		() => () => {
			releaseHoldRef.current();
			releaseHoldRef.current = noop;
		},
		[],
	);

	const setInteractionErrorIfNeeded = (
		response: T['response'],
		...args: Parameters<typeof setInteractionError>
	) => shouldSetInteractionError(response) && setInteractionError(...args);

	const handleMutation = (config: UseMutationConfig<T>) => {
		startCapturingTraceIds(operationName);
		setIsError(false);
		traceUFOPress(interactionName);

		if (fg('studio_observability_imperative_ufo_hold')) {
			releaseHoldRef.current();
			releaseHoldRef.current = interactionContext?.hold(interactionName) ?? noop;
		}

		const releaseHold = () => {
			if (!fg('studio_observability_imperative_ufo_hold')) {
				return;
			}
			releaseHoldRef.current();
			releaseHoldRef.current = noop;
		};

		const logError = <E extends { message: string }>(error: Error | E) => {
			const traceIds = getTraceIds(operationName);
			logException(error instanceof Error ? error : new Error(error.message), {
				teamName: 'rovo-agents-fe',
				loggerName: `UFOMutationError-${interactionName}`,
				...error,
				atlTraceId: traceIds?.[0] ?? 'trace-id-not-found',
			});
		};

		const disposable = commitMutation({
			...config,
			onError: (error) => {
				logError(error);
				setInteractionError(interactionName, {
					errorMessage: error.message,
					name: error.name,
				});

				setIsError(true);
				config.onError?.(error);
				stopCapturingTraceIds(operationName);
				releaseHold();
			},
			onCompleted: (response, errors) => {
				if (errors?.length) {
					const error = errors[0];
					logError(error);
					setInteractionErrorIfNeeded(response, interactionName, {
						errorMessage: error.message,
						name: error.message,
					});
					setIsError(true);
				} else {
					// Check for errors in mutation response and set interaction error if present.
					// Example response:
					// {
					// 	agentStudio_updateScenario: {
					// 		errors: [{ message: 'Error' }]
					// 	}
					// }
					for (const value of Object.values(response)) {
						if (
							!!value &&
							typeof value === 'object' &&
							'errors' in value &&
							Array.isArray(value.errors) &&
							value.errors.length
						) {
							const error = value.errors[0];
							logError(error);
							setInteractionErrorIfNeeded(response, interactionName, {
								errorMessage: error.message,
								name: error.message,
							});
							setIsError(true);
							break;
						}
					}
				}

				config.onCompleted?.(response, errors);
				stopCapturingTraceIds(operationName);
				releaseHold();
			},
		});

		return {
			dispose: () => {
				releaseHold();
				disposable.dispose();
			},
		};
	};

	const renderLegacyUFOHold = useCallback(
		() => <UFOLoadHold hold={isMutating} name={interactionName} />,
		[isMutating, interactionName],
	);

	return {
		commitMutation: handleMutation,
		isMutating,
		isError,
		renderUFOHold: fg('studio_observability_imperative_ufo_hold')
			? renderNothing
			: renderLegacyUFOHold,
	};
};
